import { BackButton } from '@repo/ui';
import c from 'classnames';
import { ContainerSizes, PageContainer, Typography } from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useFetchAndGroupCategories from 'services/insights/useFetchAndGroupCategories';
import {
  categoryAnchorId,
  getTopPriorityRankMap,
} from 'views/Results/utils/categoriesAndMetrics';
import useHashScroll from 'hooks/useHashScroll';
import styles from './AllMetrics.module.css';
import useObserve from 'hooks/useObserve';
import { useScrollDirection } from './hooks/useScrollDirection';
import MetricsCategories from './MetricsCategories';
import MetricsCategorySelector from './MetricsCategorySelector';
import MetricsFilters from './MetricsFilters';
import { filterCategories } from './MetricsFilters/helpers';
import useMetricsFilters from './MetricsFilters/useMetricsFilters';
import MetricsNavigation from './MetricsNavigation';

export default function AllMetrics() {
  const navigate = useNavigate();

  const scrollDirection = useScrollDirection(undefined, 200);

  const { categories, metrics } = useFetchAndGroupCategories();

  const { currentFilters } = useMetricsFilters();

  const filteredCategories = useMemo(
    () => filterCategories(categories, currentFilters),
    [categories, currentFilters],
  );

  const topPriorityRanks = useMemo(
    () => getTopPriorityRankMap(categories),
    [categories],
  );

  const anchorIds = useMemo(
    () => categories.map(categoryAnchorId),
    [categories],
  );

  const visibleIds = useObserve(anchorIds, {
    root: '#appWrapper',
    rootMargin: '-100px 0px -65% 0px',
  });

  useHashScroll({ elementsWithId: categories });

  if (!categories.length) return null;

  const activeId = anchorIds.find(id => visibleIds.has(id));

  const goBack = () => navigate(-1);

  return (
    <PageContainer size={ContainerSizes.LG} className={styles.pageContainer}>
      <BackButton
        goBack={goBack}
        className={c(styles.backButton, 'hide-on-desktop')}
      />
      <Breadcrumb />
      <Typography variant='heading-xl'>All Metrics</Typography>
      <div className={c(styles.floatingHeader, styles[scrollDirection])}>
        <MetricsFilters metrics={metrics} />
        <MetricsCategorySelector categories={categories} activeId={activeId} />
      </div>
      <div className={styles.dataSection}>
        <MetricsNavigation categories={categories} activeId={activeId} />
        <MetricsCategories
          categories={filteredCategories}
          topPriorityRanks={topPriorityRanks}
        />
      </div>
    </PageContainer>
  );
}
